// Утилиты для управления localStorage и предотвращения ошибки 431

// Безопасная проверка доступности localStorage
const isLocalStorageAvailable = (): boolean => {
  try {
    return typeof Storage !== 'undefined' && typeof localStorage !== 'undefined';
  } catch {
    return false;
  }
};

export const getStorageSize = (): number => {
  if (!isLocalStorageAvailable()) return 0;
  
  let totalSize = 0;
  try {
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalSize += localStorage[key].length + key.length;
      }
    }
  } catch {
    return 0;
  }
  return totalSize;
};

export const getStorageSizeKB = (): number => {
  return Math.round(getStorageSize() / 1024);
};

export const logStorageInfo = (): void => {
  if (!isLocalStorageAvailable()) {
    console.log('📦 LocalStorage is not available');
    return;
  }
  
  const sizeKB = getStorageSizeKB();
  console.log(`📦 LocalStorage size: ${sizeKB} KB`);

  // Анализ размера каждого элемента
  const items: Array<{ key: string; size: number }> = [];
  try {
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        items.push({
          key,
          size: localStorage[key].length,
        });
      }
    }
  } catch {
    console.warn('⚠️ Failed to analyze localStorage items');
    return;
  }

  // Сортировка по размеру
  items.sort((a, b) => b.size - a.size);

  if (items.length > 0) {
    console.log("Top localStorage items by size:");
    items.slice(0, 5).forEach((item) => {
      console.log(`  ${item.key}: ${Math.round(item.size / 1024)} KB`);
    });
  }

  // Предупреждение при большом размере
  if (sizeKB > 100) {
    console.warn(
      `⚠️ LocalStorage is getting large (${sizeKB} KB). Consider cleanup.`
    );
  }
};

export const cleanupStorage = (): void => {
  // Сохраняем только критичные данные
  const tokenToKeep = localStorage.getItem("token");
  const refreshTokenToKeep = localStorage.getItem("refreshToken");
  const themeToKeep = localStorage.getItem("theme");

  // Очищаем всё
  localStorage.clear();

  // Восстанавливаем критичные данные
  if (tokenToKeep) localStorage.setItem("token", tokenToKeep);
  if (refreshTokenToKeep)
    localStorage.setItem("refreshToken", refreshTokenToKeep);
  if (themeToKeep) localStorage.setItem("theme", themeToKeep);

  console.log("✅ LocalStorage cleaned up");
};

// Проверка при инициализации приложения
export const checkStorageHealth = (): void => {
  const sizeKB = getStorageSizeKB();
  const token = localStorage.getItem("token");
  const refreshToken = localStorage.getItem("refreshToken");

  if (token && token.length > 2048) {
    console.warn(`⚠️ Token is very large: ${token.length} characters`);
  }

  if (refreshToken && refreshToken.length > 2048) {
    console.warn(
      `⚠️ Refresh token is very large: ${refreshToken.length} characters`
    );
  }

  if (sizeKB > 500) {
    console.warn("⚠️ LocalStorage exceeded 500KB limit. Performing cleanup...");
    cleanupStorage();
  }

  if (process.env.NODE_ENV === "development") {
    logStorageInfo();
  }
};

export const validateTokenSize = (token: string): boolean => {
  if (token.length > 4096) {
    console.error(`❌ Token too large: ${token.length} characters`);
    return false;
  }
  return true;
};

export const getTokenInfo = (): {
  tokenSize: number;
  refreshTokenSize: number;
} => {
  const token = localStorage.getItem("token") || "";
  const refreshToken = localStorage.getItem("refreshToken") || "";

  return {
    tokenSize: token.length,
    refreshTokenSize: refreshToken.length,
  };
};
